import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Download, History } from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_LABELS = {
  created: 'Order Created',
  pending_approval: 'Pending Approval',
  pending_procurement: 'Pending Procurement',
  allocated: 'Allocated',
  ready_for_pick: 'Ready to Pick',
  picked: 'Picked',
  qa_pending: 'QA Pending',
  qa_completed: 'QA Approved',
  qa_rejected: 'QA Rejected',
  invoiced: 'Invoice Created',
  partially_dispatched: 'Partially Dispatched',
  dispatched: 'Dispatched',
  delivered: 'Delivered',
  completed: 'Completed',
  manually_billed: 'Manually Billed',
  cancelled: 'Cancelled',
};

const STATUS_BADGE = {
  pending_procurement: 'bg-amber-100 text-amber-800',
  ready_for_pick: 'bg-blue-100 text-blue-800',
  picked: 'bg-indigo-100 text-indigo-800',
  qa_pending: 'bg-purple-100 text-purple-800',
  qa_completed: 'bg-emerald-100 text-emerald-800',
  invoiced: 'bg-emerald-100 text-emerald-800',
  dispatched: 'bg-teal-100 text-teal-800',
  delivered: 'bg-teal-100 text-teal-800',
  cancelled: 'bg-red-100 text-red-700',
};

const ALLOC_LABELS = {
  fully_allocated: ['Fully Allocated', 'bg-emerald-100 text-emerald-800'],
  partially_allocated: ['Partial', 'bg-amber-100 text-amber-800'],
  not_allocated: ['Not Allocated', 'bg-red-100 text-red-700'],
  cancelled: ['—', 'bg-slate-100 text-slate-500'],
};

const SummaryTile = ({ label, value, accent, testId }) => (
  <div className={'rounded-lg border p-3 ' + (accent || 'bg-slate-50')} data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-lg font-bold">{value}</p>
  </div>
);

export const SkuOrderHistoryDialog = ({ open, onOpenChange, product }) => {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [exporting, setExporting] = React.useState(false);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (statusFilter && statusFilter !== 'all') p.set('status_filter', statusFilter);
    if (search) p.set('search', search);
    if (startDate) p.set('start_date', startDate);
    if (endDate) p.set('end_date', endDate);
    return p.toString();
  };

  const fetchHistory = React.useCallback(() => {
    if (!product) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(API + '/inventory/sku-order-history/' + product.id + '?' + buildParams(),
      { headers: { Authorization: 'Bearer ' + token } })
      .then((res) => setData(res.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load order history'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, statusFilter, search, startDate, endDate]);

  // Lazy-load: fetch only when the dialog opens (and re-fetch on filter change while open)
  React.useEffect(() => {
    if (open && product) fetchHistory();
    if (!open) { setData(null); setStatusFilter('all'); setSearch(''); setStartDate(''); setEndDate(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product, statusFilter, startDate, endDate]);

  const handleExport = () => {
    if (!product) return;
    setExporting(true);
    const token = localStorage.getItem('token');
    axios.get(API + '/inventory/sku-order-history/' + product.id + '/export.xlsx?' + buildParams(),
      { headers: { Authorization: 'Bearer ' + token }, responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sku-order-history_' + (product.product_id || product.id) + '.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Export failed'))
      .finally(() => setExporting(false));
  };

  const s = data?.summary;
  const unit = data?.product?.unit || '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="sku-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            SKU Order History — {product?.name}
            <span className="font-mono text-xs text-slate-500">{product?.product_id}</span>
          </DialogTitle>
        </DialogHeader>

        {s && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="sku-history-summary">
            <SummaryTile label="Physical Stock" value={s.physical_stock + ' ' + unit} testId="sku-summary-physical" />
            <SummaryTile label="Reserved" value={s.reserved_qty + ' ' + unit} accent="bg-blue-50" testId="sku-summary-reserved" />
            <SummaryTile label="Available" value={s.available_qty + ' ' + unit} accent="bg-emerald-50" testId="sku-summary-available" />
            <SummaryTile label="Pending Procurement" value={s.pending_procurement_qty + ' ' + unit} accent="bg-amber-50" testId="sku-summary-pending-proc" />
            <SummaryTile label="Total Ordered" value={s.total_ordered_qty + ' ' + unit} testId="sku-summary-total-ordered" />
            <SummaryTile label="Open Orders" value={s.total_open_orders} testId="sku-summary-open-orders" />
            <SummaryTile label="Completed Orders" value={s.total_completed_orders} testId="sku-summary-completed-orders" />
          </div>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <p className="text-xs text-slate-500 mb-1">Status</p>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              data-testid="sku-history-status-filter"
            >
              <option value="all">All</option>
              <option value="active">Active Orders</option>
              <option value="completed">Completed Orders</option>
              <option value="cancelled">Cancelled Orders</option>
              <option value="pending_procurement">Pending Procurement</option>
              <option value="ready_for_pick">Ready to Pick</option>
            </select>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">From</p>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 w-36" data-testid="sku-history-start-date" />
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">To</p>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9 w-36" data-testid="sku-history-end-date" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <p className="text-xs text-slate-500 mb-1">Search Order ID / Franchise ID</p>
            <div className="flex gap-1">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') fetchHistory(); }}
                placeholder="ORD-… or FRN-…"
                className="h-9"
                data-testid="sku-history-search"
              />
              <Button size="sm" variant="outline" className="h-9" onClick={fetchHistory} data-testid="sku-history-search-btn">Go</Button>
            </div>
          </div>
          <Button size="sm" variant="outline" className="h-9 gap-1" onClick={handleExport} disabled={exporting || !data} data-testid="sku-history-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export Excel
          </Button>
          <CsvDownloadButton onDownload={handleExport} testId="sku-history-export-csv" className="h-9" disabled={!data} />
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-xs" data-testid="sku-history-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left p-2 font-semibold">Order ID</th>
                  <th className="text-left p-2 font-semibold">Franchise</th>
                  <th className="text-left p-2 font-semibold">Order Date</th>
                  <th className="text-right p-2 font-semibold">Ordered</th>
                  <th className="text-right p-2 font-semibold">Allocated</th>
                  <th className="text-right p-2 font-semibold">Pending</th>
                  <th className="text-right p-2 font-semibold">Picked</th>
                  <th className="text-right p-2 font-semibold">Invoiced</th>
                  <th className="text-right p-2 font-semibold">Dispatched</th>
                  <th className="text-left p-2 font-semibold">Status</th>
                  <th className="text-left p-2 font-semibold">Allocation</th>
                  <th className="text-left p-2 font-semibold">Pick List</th>
                  <th className="text-left p-2 font-semibold">Invoice / DC</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows || []).length === 0 ? (
                  <tr><td colSpan={13} className="p-6 text-center text-slate-400" data-testid="sku-history-empty">No orders found for this SKU</td></tr>
                ) : (
                  data.rows.map((r, i) => {
                    const [allocLabel, allocCls] = ALLOC_LABELS[r.allocation_status] || [r.allocation_status, 'bg-slate-100'];
                    return (
                      <tr key={r.order_id + '-' + i} className="border-b hover:bg-slate-50" data-testid={'sku-history-row-' + i}>
                        <td className="p-2 font-mono">{r.order_number}</td>
                        <td className="p-2">
                          <span className="font-mono">{r.franchise_id}</span>
                          {r.franchise_name && <p className="text-[10px] text-slate-500">{r.franchise_name}</p>}
                        </td>
                        <td className="p-2 whitespace-nowrap">{(r.order_date || '').slice(0, 10)}</td>
                        <td className="p-2 text-right font-semibold">{r.ordered_qty}</td>
                        <td className="p-2 text-right">{r.allocated_qty}</td>
                        <td className="p-2 text-right">{r.pending_qty}</td>
                        <td className="p-2 text-right">{r.picked_qty || '-'}</td>
                        <td className="p-2 text-right">{r.invoiced_qty || '-'}</td>
                        <td className="p-2 text-right">{r.dispatched_qty || '-'}</td>
                        <td className="p-2">
                          <Badge className={'text-[10px] ' + (STATUS_BADGE[r.status] || 'bg-slate-100 text-slate-700')}>
                            {STATUS_LABELS[r.status] || r.status}
                          </Badge>
                          {r.blocker_reason && r.status === 'pending_procurement' && (
                            <p className="text-[10px] text-amber-700 mt-0.5 max-w-[180px]">{r.blocker_reason}</p>
                          )}
                        </td>
                        <td className="p-2"><Badge className={'text-[10px] ' + allocCls}>{allocLabel}</Badge></td>
                        <td className="p-2 font-mono">{r.pick_list_number || '-'}</td>
                        <td className="p-2 font-mono">{r.invoice_number || r.dc_number || '-'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        {data && (
          <p className="text-xs text-slate-500">{data.total_rows} order line(s) shown</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SkuOrderHistoryDialog;
